/**
 * EngagementTimelineView — per-template drill panel inside the
 * Insights tab. Lazy-fetches `/engagement` on mount, shows
 * aggregates + visit log + notification log, and gates a
 * "Re-engage" button on `is_went_cold`. Test notification button is
 * dry-run by default.
 */
import React, { useState, useCallback } from 'react';
import { Button } from '../../ui/button';
import {
  Loader2, ArrowLeft, BellRing, Send, Eye, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { ReengagePanel } from './ReengagePanel';
import { EngagementBadge, InsightStat, formatRelative, formatDuration } from './_shared';

export const EngagementTimelineView = ({ templateId, templateName, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testNotifyOpen, setTestNotifyOpen] = useState(false);
  const [testNotifyData, setTestNotifyData] = useState(null);
  const [testNotifyLoading, setTestNotifyLoading] = useState(false);
  const [reengageOpen, setReengageOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/demo-director/templates/${templateId}/engagement`);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load engagement timeline');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const runTestNotify = async (send) => {
    setTestNotifyLoading(true);
    try {
      const res = await api.post(
        `/admin/demo-director/templates/${templateId}/test-notify?send=${send ? 'true' : 'false'}`,
      );
      setTestNotifyData(res.data);
      if (send) {
        toast.success(
          `Notification fired — Slack: ${res.data.slack_sent ? '✓' : '✗'} · Email: ${res.data.email_sent ? '✓' : '✗'}`,
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test notification failed');
    } finally {
      setTestNotifyLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-xs text-slate-500" data-testid="dd-engagement-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading timeline…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="py-16 text-center text-xs text-slate-500" data-testid="dd-engagement-empty">
        No engagement data.
        <Button size="sm" variant="ghost" onClick={onBack} className="ml-2 text-fuchsia-600">
          <ArrowLeft className="h-3 w-3 mr-1" /> Back
        </Button>
      </div>
    );
  }

  const a = data.aggregates || {};
  const visits = data.visits || [];
  const notifications = data.notifications || [];

  if (reengageOpen) {
    return (
      <ReengagePanel
        templateId={templateId}
        templateName={templateName}
        totalVisits={data.aggregates?.total_visits ?? 0}
        industryPreset={data.template?.industry_preset || null}
        onBack={() => setReengageOpen(false)}
      />
    );
  }

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="dd-engagement-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button
          size="sm" variant="ghost" onClick={onBack}
          className="h-7 text-[11px] text-slate-600 hover:text-fuchsia-600"
          data-testid="dd-engagement-back-btn"
        >
          <ArrowLeft className="h-3 w-3 mr-1" /> Back to leaderboard
        </Button>
        <div className="flex items-center gap-2">
          {data.is_went_cold && (
            <Button
              size="sm" onClick={() => setReengageOpen(true)}
              className="h-7 text-[11px] gap-1 bg-rose-600 text-white hover:bg-rose-700"
              data-testid="dd-engagement-reengage-btn"
            >
              <Sparkles className="h-3 w-3" /> Re-engage
            </Button>
          )}
          <Button
            size="sm" variant="outline"
            onClick={() => { setTestNotifyOpen((o) => !o); if (!testNotifyData) runTestNotify(false); }}
            className="h-7 text-[11px] gap-1 border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50"
            data-testid="dd-engagement-test-notify-btn"
          >
            <BellRing className="h-3 w-3" />
            {testNotifyOpen ? 'Hide test notification' : 'Test notification'}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-800">{templateName}</h3>
        {data.engagement && (
          <EngagementBadge
            engagement={data.engagement} size="lg"
            testId="dd-engagement-score-badge"
          />
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <InsightStat label="Total visits" value={a.total_visits ?? 0} testId="dd-engagement-stat-total-visits" />
        <InsightStat
          label="Time-to-first-open" value={formatDuration(a.time_to_first_open_sec)}
          testId="dd-engagement-stat-ttfo"
        />
        <InsightStat label="Avg gap" value={formatDuration(a.avg_gap_sec)} testId="dd-engagement-stat-avg-gap" />
        <InsightStat label="Alerts fired" value={a.notifications_fired ?? 0} highlight testId="dd-engagement-stat-alerts-fired" />
      </div>

      {testNotifyOpen && (
        <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50/40 p-3 space-y-2" data-testid="dd-engagement-test-notify-panel">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-xs font-semibold text-fuchsia-800 flex items-center gap-1">
              <BellRing className="h-3 w-3" /> Test notification preview
              <span className="text-[10px] font-normal text-fuchsia-600">
                {testNotifyData?.dry_run ? '(dry-run — nothing sent yet)' : '(real fire)'}
              </span>
            </h4>
            <Button
              size="sm" onClick={() => runTestNotify(true)}
              disabled={testNotifyLoading}
              className="h-7 text-[11px] gap-1 bg-fuchsia-600 text-white hover:bg-fuchsia-700"
              data-testid="dd-engagement-test-notify-send-btn"
            >
              {testNotifyLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              Send for real
            </Button>
          </div>
          {testNotifyLoading && !testNotifyData ? (
            <div className="text-[11px] text-fuchsia-700">Building preview…</div>
          ) : testNotifyData ? (
            <>
              <div className="rounded border border-fuchsia-100 bg-white p-2 text-[11px] whitespace-pre-line text-slate-700" data-testid="dd-engagement-test-notify-slack-text">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Slack</p>
                {testNotifyData.slack_text}
              </div>
              <div className="rounded border border-fuchsia-100 bg-white p-2 text-[11px]" data-testid="dd-engagement-test-notify-email">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                  Email — to {testNotifyData.creator_email || <em className="text-slate-400">no creator email resolved</em>}
                </p>
                <p className="text-slate-800 font-medium">{testNotifyData.email.subject}</p>
              </div>
              {!testNotifyData.dry_run && (
                <div className="text-[11px] text-fuchsia-700">
                  Slack: {testNotifyData.slack_sent ? '✓ sent' : '✗ failed'} ·
                  Email: {testNotifyData.email_sent ? '✓ sent' : '✗ skipped/failed'}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      <div className="rounded-lg border border-slate-100 bg-white p-3" data-testid="dd-engagement-visits">
        <h4 className="text-xs font-semibold text-slate-700 mb-2">Visit log ({visits.length})</h4>
        {visits.length === 0 ? (
          <p className="text-[11px] text-slate-400 py-3">No visits yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visits.map((v) => (
              <li
                key={v.id}
                className="py-1.5 text-[11px] flex items-center justify-between gap-2"
                data-testid={`dd-engagement-visit-${v.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Eye className="h-3 w-3 text-slate-400 flex-none" />
                  <span className="text-slate-700 tabular-nums">{formatRelative(v.ts)}</span>
                  <span className="text-slate-400 truncate">{v.ip || '—'}</span>
                </div>
                <span className="text-[9px] text-slate-400 truncate max-w-[40%]">
                  {(v.user_agent || '').slice(0, 60)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-fuchsia-100 bg-fuchsia-50/40 p-3" data-testid="dd-engagement-notifications">
        <h4 className="text-xs font-semibold text-fuchsia-800 mb-2">
          Alerts fired ({notifications.length})
        </h4>
        {notifications.length === 0 ? (
          <p className="text-[11px] text-fuchsia-700/70">No alerts have fired for this template yet.</p>
        ) : (
          <ul className="space-y-1">
            {notifications.map((n, i) => (
              <li
                key={`${n.ts}-${i}`}
                className="text-[11px] text-fuchsia-800 flex items-center justify-between gap-2"
                data-testid={`dd-engagement-notification-${i}`}
              >
                <span className="flex items-center gap-1">
                  <BellRing className="h-3 w-3 text-fuchsia-600" />
                  {formatRelative(n.ts)}
                  {n.is_first_open && (
                    <span className="ml-1 text-[9px] font-semibold uppercase tracking-wider text-emerald-700">first-open</span>
                  )}
                </span>
                <span className="text-[10px] text-fuchsia-700/80">
                  Slack {n.slack_ok ? '✓' : '✗'} · Email {n.email_ok ? '✓' : '✗'}
                  {n.email_recipient && <span className="ml-1 text-fuchsia-600/70">→ {n.email_recipient}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
